
import React from "react";
import AuraBadge from "@/components/common/AuraBadge";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface UserCardProps {
  user: {
    id: string;
    username: string;
    display_name?: string;
    profile_image_url?: string;
    banner_image_url?: string;
    registration_number?: number;
    elo_rating?: number;
    is_admin?: boolean;
    is_premium?: boolean;
  };
}

export const UserCard: React.FC<UserCardProps> = ({ user }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/profile/${user.username}`);
  };

  return (
    <Card 
      className="relative overflow-hidden cursor-pointer border-gray-800 p-0 group"
      onClick={handleClick}
    >
      {/* Background with banner image */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1A1F2C] to-[#252A38] opacity-90">
        {user.banner_image_url && (
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-30"
            style={{ backgroundImage: `url(${user.banner_image_url})` }}
          />
        )}
      </div>
      
      <div className="relative z-10 p-4 flex items-center">
        {/* Avatar section */}
        <div className="mr-3">
          <Avatar className={cn(
            "w-12 h-12",
            user.is_admin && "ring-2 ring-[#F97316]",
            user.is_premium && "ring-2 ring-[#E5DEFF]",
            !user.is_admin && !user.is_premium && "ring-2 ring-primary/20"
          )}>
            <AvatarImage 
              src={user.profile_image_url} 
              alt={user.display_name || user.username}
            />
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/40 text-white">
              {(user.display_name?.charAt(0) || user.username?.charAt(0) || "U").toUpperCase()}
            </AvatarFallback>
          </Avatar>
        </div>
        
        {/* User info section */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-sm truncate">
              {user.display_name || user.username}
            </h3>
            
            {/* Aura score */}
            {user.elo_rating !== undefined && (
              <AuraBadge value={user.elo_rating} size="sm" />
            )}
          </div>
          
          <div className="flex items-center text-xs text-gray-400">
            <span className="truncate">@{user.username}</span>
            
            {/* User number */}
            {user.registration_number && (
              <span className="ml-1 bg-[#252A38] px-1.5 py-0.5 rounded text-[10px]">
                #{user.registration_number}
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Hover effect */}
      <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </Card>
  );
};
